import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactSection({ isVisible }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you for your message! I'll be in touch soon.");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className={`py-24 lg:py-32 bg-[#EAE3D9] transition-all duration-700 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      data-testid="contact-section"
    >
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1A1A1A] mb-6 heading-decorated-center">
            Get in Touch
          </h2>
          <p className="font-['Manrope'] text-base lg:text-lg text-[#4A4542]">
            If you're curious about working together, I'd love to hear from you.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
          {/* Name */}
          <div className="space-y-2">
            <Label
              htmlFor="name"
              className="font-['Manrope'] text-sm uppercase tracking-wider text-[#4A4542]"
            >
              Name
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your name"
              className="input-elegant bg-transparent border-0 border-b border-[#C5A059]/40 rounded-none px-0 py-4 text-[#1A1A1A] placeholder:text-[#4A4542]/50 focus-visible:ring-0 focus-visible:border-[#8B5A2B]"
              data-testid="contact-name-input"
            />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label
              htmlFor="email"
              className="font-['Manrope'] text-sm uppercase tracking-wider text-[#4A4542]"
            >
              Email
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              className="input-elegant bg-transparent border-0 border-b border-[#C5A059]/40 rounded-none px-0 py-4 text-[#1A1A1A] placeholder:text-[#4A4542]/50 focus-visible:ring-0 focus-visible:border-[#8B5A2B]"
              data-testid="contact-email-input"
            />
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label
              htmlFor="message"
              className="font-['Manrope'] text-sm uppercase tracking-wider text-[#4A4542]"
            >
              Message
            </Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell me about what you're looking for..."
              rows={5}
              className="bg-transparent border-0 border-b border-[#C5A059]/40 rounded-none px-0 py-4 text-[#1A1A1A] placeholder:text-[#4A4542]/50 focus-visible:ring-0 focus-visible:border-[#8B5A2B] resize-none"
              data-testid="contact-message-input"
            />
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto btn-primary px-10 py-6 text-base font-['Manrope'] font-medium tracking-wide rounded-none"
              data-testid="contact-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={18} strokeWidth={1.5} className="mr-2" />
                  Contact Me
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}
